import { useState } from 'react'
import {
  SelectOptionType,
  TextHeader,
  showInfoMessage,
  showSuccessMessage,
  showErrorMessage,
  triggerPrompt,
  Modal,
  Button,
  Text,
  Checkbox,
} from '@eltoro-ui/components'
import {
  Campaignservicev1Campaign,
  Creativeservicev1AdType,
  Creativeservicev1Creative,
  Creativeservicev1Type,
  Campaignservicev1Creative,
  Campaignservicev1OrderLine,
} from 'next-gen-sdk'
import { useLocation } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import { CreativesLibraryPage, typeGetter } from 'Pages'
import { useAppContext, useCampaignContext } from 'Contexts'
import {
  CreativeUpload,
  GoBackButton,
  AdTagCreate,
  NativeBannerCreate,
} from 'Components'
import { poll } from 'Helpers'
import { getApiConfig } from 'Requests'
import { EditingCancelUpdateButtons } from '../EditingCancelUpdateButtons'
import { ModifiedOrderLineType } from '../types'
import { AssetEditHeader } from '../AssetEditHeader'
import { CreativeEditOLTable } from './CreativeEditOLTable'
import { IMPRESSION_MIN } from '../shared'
import { dayjs } from 'Tools/dateUtils'

export const CreativeEdit = ({
  className = '',
  selectedOls,
  campaign,
  onCloseModal,
  filterRules,
  setRefreshCampaign,
}: {
  className?: string
  selectedOls: Campaignservicev1OrderLine[]
  campaign: Campaignservicev1Campaign
  onCloseModal: () => void
  filterRules?: Creativeservicev1AdType
  setRefreshCampaign?: (x: boolean) => void
}) => {
  const [view, setView] = useState<'library' | 'create'>()
  // prettier-ignore
  const [
      selectedCreativeType,
      setSelectedCreativeType,
    ] = useState<SelectOptionType | undefined>({
      value: 'banner',
      label: 'Banner',
    })
  const [modifiedOrderLines, setModifiedOrderLines] = useState<
    ModifiedOrderLineType[]
  >(selectedOls)
  const [disabled, setDisabled] = useState(false)
  const [showBrandUrlDialog, setShowBrandUrlDialog] = useState(false)
  const [creativesNeedingBrandUrl, setCreativesNeedingBrandUrl] = useState<
    Array<{
      creative: Creativeservicev1Creative
      orderLineNames: string[]
      availableBrandUrls: Array<{
        url: string
        sourceCreativeId: string
        sourceCreativeName?: string
      }>
      selectedBrandUrls: string[]
    }>
  >([])
  const { currentOrg, campaignServiceApi, creativeServiceApi } = useAppContext()
  const { setRefresh, isPolitical } = useCampaignContext()
  const { pathname } = useLocation()
  const isInCampaignCreation =
    pathname.includes('/campaigns/create') ||
    pathname.includes('/campaigns/edit')

  const isCompatible = (
    creativeToAdd: Creativeservicev1Creative,
    orderLine: ModifiedOrderLineType,
  ) => {
    // Note/TODO: the order line ad type is coming back as UNSPECIFIED for now, sunflower is aware
    // Will have to use ad type found on attached creatives for now
    // https://eltoroteam.slack.com/archives/C029B5WPMU3/p1671649180237709
    // if (orderLine.adType === 'ORDERLINE_ADTYPE_UNSPECIFIED') return true
    // const olCreativeType = orderLine.adType?.split('_').slice(-1)[0]
    // Creativeservicev1AdType = "CREATIVE_ADTYPE_UNSPECIFIED" | "CREATIVE_ADTYPE_BANNER" | "CREATIVE_ADTYPE_VIDEO" | "CREATIVE_ADTYPE_NATIVE" | "CREATIVE_ADTYPE_OTT"
    // Campaignservicev1AdType = "ORDERLINE_ADTYPE_UNSPECIFIED" | "ORDERLINE_ADTYPE_BANNER" | "ORDERLINE_ADTYPE_VIDEO" | "ORDERLINE_ADTYPE_NATIVE" | "ORDERLINE_ADTYPE_OTT"
    if (!orderLine.creatives || orderLine.creatives.length === 0) return true // for now, allow adding to order line with no creatives
    const olCreativeAdType = orderLine.creatives[0].adType
      ?.split('_')
      .slice(-1)[0]
    const creativeAdType = creativeToAdd.adType?.split('_').slice(-1)[0]
    return olCreativeAdType === creativeAdType
  }

  // Detect creatives that could inherit brand URLs from other creatives in the same order line
  const detectCreativesNeedingBrandUrl = async () => {
    const creativeToOrderLinesMap = new Map<
      string,
      {
        creative: Creativeservicev1Creative
        orderLines: Array<{
          orderLine: ModifiedOrderLineType
          availableBrandUrls: Array<{
            url: string
            sourceCreativeId: string
            sourceCreativeName?: string
          }>
        }>
      }
    >()

    // First, collect all newly added creatives and their associated order lines
    for (const modOL of modifiedOrderLines) {
      if (!modOL.creatives || modOL.creatives.length <= 1) continue

      // Get the original order line to compare
      const originalOL = selectedOls.find((ol) => ol.id === modOL.id)
      if (!originalOL) continue

      // Find newly added creatives (in modified but not in original)
      const newlyAddedCreatives = modOL.creatives.filter(
        (creative) =>
          !originalOL.creatives?.some((orig) => orig.id === creative.id),
      )

      if (newlyAddedCreatives.length === 0) continue

      // Collect all available brand URLs from existing creatives in this order line
      const availableBrandUrls: Array<{
        url: string
        sourceCreativeId: string
        sourceCreativeName?: string
      }> = []

      for (const existingCreative of modOL.creatives) {
        if (
          newlyAddedCreatives.some(
            (newCreative) => newCreative.id === existingCreative.id,
          )
        ) {
          continue // Skip newly added creatives
        }

        if (existingCreative.id && currentOrg?.id) {
          try {
            const fullCreative = await creativeServiceApi?.advertisingPlatformServiceGetCreative(
              existingCreative.id,
              currentOrg.id,
            )
            const brandUrl =
              fullCreative?.nativeMetadata?.alternativeMobileLandingPageUrl
            if (brandUrl) {
              // Only add if we don't already have this URL
              if (!availableBrandUrls.some((item) => item.url === brandUrl)) {
                availableBrandUrls.push({
                  url: brandUrl,
                  sourceCreativeId: existingCreative.id,
                  sourceCreativeName: `Creative ${existingCreative.id}`,
                })
              }
            }
          } catch (error) {
            console.error('Error checking creative brand URL:', error)
          }
        }
      }

      // Group newly added creatives by creative ID
      if (availableBrandUrls.length > 0) {
        for (const newCreative of newlyAddedCreatives) {
          const creativeId = newCreative.id
          if (!creativeId) continue

          if (!creativeToOrderLinesMap.has(creativeId)) {
            creativeToOrderLinesMap.set(creativeId, {
              creative: newCreative,
              orderLines: [],
            })
          }

          creativeToOrderLinesMap.get(creativeId)!.orderLines.push({
            orderLine: modOL,
            availableBrandUrls,
          })
        }
      }
    }

    // Convert to the format expected by the UI, with all brand URLs from all order lines
    const creativesToUpdate: Array<{
      creative: Creativeservicev1Creative
      orderLineNames: string[]
      availableBrandUrls: Array<{
        url: string
        sourceCreativeId: string
        sourceCreativeName?: string
      }>
      selectedBrandUrls: string[]
    }> = []

    for (const [, data] of creativeToOrderLinesMap) {
      // Collect all unique brand URLs from all order lines this creative will be added to
      const allBrandUrls = new Map<
        string,
        {
          url: string
          sourceCreativeId: string
          sourceCreativeName?: string
        }
      >()

      const orderLineNames: string[] = []

      for (const orderLineData of data.orderLines) {
        orderLineNames.push(
          orderLineData.orderLine.name || 'Unnamed Order Line',
        )

        for (const brandUrl of orderLineData.availableBrandUrls) {
          if (!allBrandUrls.has(brandUrl.url)) {
            allBrandUrls.set(brandUrl.url, brandUrl)
          }
        }
      }

      const availableBrandUrls = Array.from(allBrandUrls.values())

      if (availableBrandUrls.length > 0) {
        creativesToUpdate.push({
          creative: data.creative,
          orderLineNames,
          availableBrandUrls,
          selectedBrandUrls: [], // Start with no selections
        })
      }
    }

    return creativesToUpdate
  }

  // Updates the campaign order lines on submit
  // Note- this component is also on the dashboard, so it shouldn't use methods from CampaignContext
  const handleUpdate = async () => {
    setDisabled(true)

    // Check if there are creatives that need brand URL updates
    const creativesToUpdate = await detectCreativesNeedingBrandUrl()

    if (creativesToUpdate.length > 0) {
      // Check if all creatives have exactly one brand URL option - if so, auto-apply
      const allHaveSingleBrandUrl = creativesToUpdate.every(
        (item) => item.availableBrandUrls.length === 1,
      )

      if (allHaveSingleBrandUrl) {
        // Auto-apply the single brand URL to each creative
        const creativesWithAutoSelectedUrls = creativesToUpdate.map((item) => ({
          ...item,
          selectedBrandUrls: [item.availableBrandUrls[0].url],
        }))

        // Set the state with auto-selected URLs
        setCreativesNeedingBrandUrl(creativesWithAutoSelectedUrls)

        // Apply brand URL updates directly with the auto-selected URLs
        await applyBrandUrlUpdates(creativesWithAutoSelectedUrls)

        // Then proceed with normal order line updates
        await performOrderLineUpdates()
        return
      } else {
        // Show dialog for user choice when multiple options exist
        setCreativesNeedingBrandUrl(creativesToUpdate)
        setShowBrandUrlDialog(true)
        setDisabled(false) // Re-enable until user makes a decision
        return
      }
    }

    // Proceed with normal update
    await performUpdate()
  }

  // Helper function to apply brand URL updates to selected creatives
  const applyBrandUrlUpdates = async (
    creativesToUpdate: Array<{
      creative: Creativeservicev1Creative
      orderLineNames: string[]
      availableBrandUrls: Array<{
        url: string
        sourceCreativeId: string
        sourceCreativeName?: string
      }>
      selectedBrandUrls: string[]
    }>,
  ) => {
    // Apply brand URL updates for selected creatives
    for (const item of creativesToUpdate) {
      // Only update if at least one brand URL is selected
      if (
        item.selectedBrandUrls.length > 0 &&
        item.creative.id &&
        currentOrg?.id
      ) {
        // Since a creative can only have one brand URL, use the first selected one
        const [brandUrlToApply] = item.selectedBrandUrls

        try {
          const fullCreative = await creativeServiceApi?.advertisingPlatformServiceGetCreative(
            item.creative.id,
            currentOrg.id,
          )
          await creativeServiceApi?.advertisingPlatformServiceUpdateCreative(
            item.creative.id,
            currentOrg.id,
            {
              nativeMetadata: {
                alternativeMobileLandingPageUrl: brandUrlToApply,
              },
            },
            fullCreative?.nativeMetadata !== null
              ? 'native_metadata.alternative_mobile_landing_page_url'
              : '',
          )
        } catch (error) {
          console.error('Error updating creative brand URL:', error)
          showErrorMessage(
            'Failed to update brand URL for some creatives',
            'The order line changes will still be saved.',
          )
        }
      }
    }
  }

  // Helper function to perform order line updates
  const performOrderLineUpdates = async () => {
    modifiedOrderLines.forEach(async (modOL) => {
      const originalOL = selectedOls.find((ol) => ol.id === modOL.id)
      // add brandURLs to creatives
      // if has brandUrls and only 1, add the first one to the creatives
      if (modOL.brandUrlEdited && modOL.creatives) {
        // add the first brand url to all creatives
        if (modOL.brandUrls?.[0]) {
          await Promise.all(
            modOL.creatives.map(async (creative) => {
              if (!creative.id || !currentOrg?.id || !creativeServiceApi) return
              const fullCreative = await creativeServiceApi.advertisingPlatformServiceGetCreative(
                creative.id,
                currentOrg.id,
              )
              creativeServiceApi.advertisingPlatformServiceUpdateCreative(
                creative.id,
                currentOrg.id,
                {
                  nativeMetadata: {
                    alternativeMobileLandingPageUrl: modOL.brandUrls?.[0],
                  },
                },
                fullCreative.nativeMetadata !== null
                  ? 'native_metadata.alternative_mobile_landing_page_url'
                  : '',
              )
            }),
          )
        }
        if (modOL.brandUrls === undefined) {
          // remove brand url (if undefined and edited)
          await Promise.all(
            modOL.creatives.map(async (creative) => {
              if (!creative.id || !currentOrg?.id || !creativeServiceApi) return
              const fullCreative = await creativeServiceApi.advertisingPlatformServiceGetCreative(
                creative.id,
                currentOrg.id,
              )
              creativeServiceApi.advertisingPlatformServiceUpdateCreative(
                creative.id,
                currentOrg.id,
                {
                  nativeMetadata: {
                    alternativeMobileLandingPageUrl: undefined,
                  },
                },
                fullCreative.nativeMetadata !== null
                  ? 'native_metadata.alternative_mobile_landing_page_url'
                  : '',
              )
            }),
          )
        }
      }
      // update the existing order line
      if (originalOL && modOL.id) {
        const creativeIdsToAttach = modOL?.creatives
          ?.map((c) => {
            if (
              !originalOL.creatives?.find(
                (originalAud) => originalAud.id === c.id,
              )
            ) {
              return c.id || undefined
            }
            return undefined
          })
          .filter((c) => c !== undefined) as string[]
        const creativeIdsToDetach = originalOL?.creatives
          ?.map((c) => {
            if (
              !modOL.creatives?.find((modAud) => modAud.id === c.id) &&
              originalOL.creatives?.find(
                (originalAud) => originalAud.id === c.id,
              )
            ) {
              return c.id || undefined
            }
            return undefined
          })
          .filter((c) => c !== undefined) as string[]

        // Attach new creatives to existing order lines
        // Remove swapped out creatives to order lines
        try {
          if (
            creativeIdsToAttach &&
            modOL.id &&
            creativeIdsToAttach.length > 0
          ) {
            await campaignServiceApi
              ?.advertisingPlatformServiceBatchAddCreatives(modOL.id, {
                orgId: currentOrg?.id,
                creativeIds: creativeIdsToAttach,
              })
              .then((res) => {
                if (res) {
                  showSuccessMessage(
                    "Your order line's creatives were updated",
                    '',
                  )
                }
                return res
              })
          }
          if (creativeIdsToDetach && creativeIdsToDetach.length > 0) {
            await campaignServiceApi
              ?.advertisingPlatformServiceBatchRemoveCreatives(modOL.id, {
                orgId: currentOrg?.id,
                creativeIds: creativeIdsToDetach,
              })
              .then((res) => {
                if (res) {
                  showSuccessMessage(
                    "Your order line's creatives were updated",
                    '',
                  )
                }
                return res
              })
          }
          // Change clickthru if needed
          if (
            originalOL.clickThroughUrl !== modOL.clickThroughUrl &&
            originalOL.id &&
            originalOL.orgId
          ) {
            await campaignServiceApi
              ?.advertisingPlatformServiceUpdateOrderLine(
                originalOL.id,
                originalOL.orgId,
                {
                  clickThroughUrl: modOL.clickThroughUrl,
                },
              )
              .then((res) => {
                showSuccessMessage('Click-through Url updated successfully', '')
              })
              .catch((err) => {
                if (err) {
                  showErrorMessage(
                    'Click-through Url did not update correctly',
                    '',
                  )
                }
              })
          }
        } finally {
          if (isInCampaignCreation) setRefresh(true)
          if (setRefreshCampaign) {
            setRefreshCampaign(true)
          }
          onCloseModal()
        }
      }

      // if this is a new order line, create it
      if (modOL.tempId && modOL.creatives) {
        // create
        if (!campaignServiceApi || !currentOrg?.id) return
        const now = dayjs().tz().add(1, 'day').startOf('day').utc().toDate()
        const nowPlusSeven = dayjs()
          .tz()
          .add(8, 'days')
          .endOf('day')
          .utc()
          .toDate()

        const orderLineCreationParameters = {
          name: modOL.name,
          impressions: currentOrg?.minimumImpressions || IMPRESSION_MIN,
          startTime: now,
          endTime: nowPlusSeven,
          clickThroughUrl: modOL.clickThroughUrl,
        }

        const createdOL = await campaignServiceApi.advertisingPlatformServiceCreateOrderLine(
          currentOrg.id,
          orderLineCreationParameters,
          campaign?.id,
        )
        if (!createdOL?.id) return

        // waiting for draft status before attaching creatives (otherwise OL gets stuck in 'creating' status)
        const newOrderLine = await poll(
          (token) =>
            campaignServiceApi.advertisingPlatformServiceGetOrderLine(
              createdOL.id || '',
              currentOrg.id ? currentOrg.id : '',
              token ? getApiConfig(token) : undefined,
            ),
          (ol?: Campaignservicev1OrderLine) =>
            ol?.status !== 'ORDERLINE_STATUS_DRAFT',
          1000,
          900000,
        )
        if (!newOrderLine?.id) return

        try {
          // attach creatives
          const creativeIdsToAttach = modOL.creatives
            .map((c) => c.id || undefined)
            .filter((c) => c !== undefined) as string[]
          if (creativeIdsToAttach && creativeIdsToAttach.length > 0) {
            await campaignServiceApi
              .advertisingPlatformServiceBatchAddCreatives(newOrderLine.id, {
                orgId: currentOrg.id,
                creativeIds: creativeIdsToAttach,
              })
              .then((res) => {
                if (res && modOL?.creatives) {
                  showInfoMessage(
                    'Order Line Created',
                    `Your selected ${
                      modOL?.creatives.length > 1 ? 'creatives' : 'creative'
                    } did not have a match order line. We created one for you.`,
                  )
                }
                return res
              })
          }
        } finally {
          if (isInCampaignCreation) setRefresh(true)
          if (setRefreshCampaign) {
            setRefreshCampaign(true)
          }
          onCloseModal()
        }
      }
    })
  }

  const performUpdate = async () => {
    setDisabled(true)

    // Apply brand URL updates for selected creatives from state
    await applyBrandUrlUpdates(creativesNeedingBrandUrl)

    // Then perform order line updates
    await performOrderLineUpdates()
  }

  const handleAddChange = async (modifiedOL: ModifiedOrderLineType) => {
    setModifiedOrderLines((prevModifiedOLs) => {
      if (modifiedOL.tempId) {
        // if it has a temp id - (is new)
        if (!modifiedOL.creatives?.length)
          // if it does not have creatives, remove it
          return prevModifiedOLs.filter((ol) => ol.tempId !== modifiedOL.tempId)

        const prevIndex = prevModifiedOLs.findIndex(
          (p) => p.tempId === modifiedOL.tempId,
        )
        // if it existed, update in place
        if (prevIndex >= 0) {
          return [
            ...prevModifiedOLs.slice(0, prevIndex),
            modifiedOL,
            ...prevModifiedOLs.slice(prevIndex + 1),
          ]
        }
        // otherwise, add it to the end
        return [...prevModifiedOLs, modifiedOL]
      }

      // update non-temp ol in place
      const prevIndex = prevModifiedOLs.findIndex((p) => p.id === modifiedOL.id)
      return [
        ...prevModifiedOLs.slice(0, prevIndex),
        modifiedOL,
        ...prevModifiedOLs.slice(prevIndex + 1),
      ]
    })
  }

  const handleUpdateClickThru = (
    clickThroughUrl: string,
    modifiedOL: ModifiedOrderLineType,
  ) => {
    handleAddChange({ ...modifiedOL, clickThroughUrl })
  }

  const handleAddCreative = (
    newCreative: Creativeservicev1Creative,
    orderLine?: ModifiedOrderLineType,
  ) => {
    if (
      orderLine &&
      isCompatible(newCreative, orderLine) &&
      !orderLine.creatives?.find((c) => c.id === newCreative.id)
    ) {
      // add to order line
      handleAddChange({
        ...orderLine,
        creatives: [...(orderLine.creatives || []), newCreative],
      })
      return
    }

    // Refactor'd due to weirdness with the uploader
    setModifiedOrderLines((prevModifiedOrderLines) => {
      const compatibleOrderLines = prevModifiedOrderLines.some((ol) =>
        isCompatible(newCreative, ol),
      )
      if (compatibleOrderLines) {
        // add to compatible order lines
        return prevModifiedOrderLines.map((modOl) => {
          return {
            ...modOl,
            creatives: isCompatible(newCreative, modOl)
              ? [...(modOl.creatives || []), newCreative]
              : modOl.creatives,
          }
        })
      } else {
        // if not compatible, add new order line
        const tempOrderLineNames = [
          ...new Set(
            prevModifiedOrderLines.reduce((acc: string[], ol) => {
              if (ol.tempId && ol.name) return [...acc, ol.name]
              return acc
            }, []),
          ),
        ]
        let orderLineCount = campaign.orderLines?.length || 0
        if (tempOrderLineNames.length > 0) {
          orderLineCount = Number(
            tempOrderLineNames.slice(-1)[0].split('_').slice(-1),
          )
        }
        const newOrderLine = {
          name: `${
            campaign?.name ? `${campaign.name}_` : ''
          }${newCreative.type?.split('_').slice(-1)}_Draft_${
            orderLineCount + 1
          }`,
          creatives: [newCreative],
          tempId: uuid(),
        }
        // add new order line
        return [...prevModifiedOrderLines, newOrderLine]
      }
    })
  }

  const handleRemoveCreative = (
    orderLine: ModifiedOrderLineType,
    creative?: Creativeservicev1Creative,
  ) => {
    // do not remove if last creative on non-draft OL
    if (
      orderLine.creatives &&
      orderLine.creatives.length <= 1 &&
      orderLine.status !== 'ORDERLINE_STATUS_DRAFT' &&
      !orderLine.tempId
    )
      return
    // if no creative specified, remove all creatives from order line
    if (!creative) {
      handleAddChange({ ...orderLine, creatives: [] })
    } else {
      // otherwise, remove specified creative from order line
      const filteredCreatives = orderLine.creatives?.filter(
        (c) => c.id !== creative.id,
      )
      handleAddChange({ ...orderLine, creatives: filteredCreatives })
    }
  }

  const handleUpdateBrandURL = (
    brandUrls: string[],
    modifiedOL: ModifiedOrderLineType,
  ) => {
    handleAddChange({ ...modifiedOL, brandUrls, brandUrlEdited: true })
  }

  // Check if any creative in the order line has deployed attachments to other order lines
  const checkForDeployedCreativeUsage = async (
    modifiedOL: ModifiedOrderLineType,
  ): Promise<boolean> => {
    if (!modifiedOL.creatives || !currentOrg?.id) return false

    const deployedStatuses = [
      'ORDERLINE_STATUS_DEPLOYED',
      'ORDERLINE_STATUS_SERVING',
      'ORDERLINE_STATUS_COMPLETED',
      'ORDERLINE_STATUS_ARCHIVED',
      'ORDERLINE_STATUS_PAUSED',
      'ORDERLINE_STATUS_ERRORED_AND_SERVING',
      'ORDERLINE_STATUS_ERRORED_AND_DEPLOYED',
    ]

    try {
      const creativeChecks = await Promise.all(
        modifiedOL.creatives.map(async (creative) => {
          if (!creative.id || !currentOrg?.id) return false

          // Get the full creative with its order line attachments
          const fullCreative = await creativeServiceApi?.advertisingPlatformServiceGetCreative(
            creative.id,
            currentOrg.id,
          )

          if (!fullCreative?.orderLines?.length) return false

          // Check if any attached order lines are deployed
          const attachedOrderLines = await Promise.all(
            fullCreative.orderLines.map(async (ol) => {
              if (!ol.id || !currentOrg?.id) return null
              try {
                return await campaignServiceApi?.advertisingPlatformServiceGetOrderLine(
                  ol.id,
                  currentOrg.id,
                )
              } catch {
                return null
              }
            }),
          )

          // Filter out null results and check for deployed status
          const validOrderLines = attachedOrderLines.filter(
            (ol) => ol && ol.id !== modifiedOL.id, // Exclude the current order line
          )

          return validOrderLines.some((ol) =>
            deployedStatuses.includes(ol?.status || ''),
          )
        }),
      )

      return creativeChecks.some(Boolean)
    } catch (error) {
      console.error('Error checking deployed creative usage:', error)
      return false
    }
  }

  const handleUpdateBrandURLWithConfirmation = async (
    brandUrls: string[],
    modifiedOL: ModifiedOrderLineType,
  ) => {
    const hasDeployedUsage = await checkForDeployedCreativeUsage(modifiedOL)

    if (hasDeployedUsage) {
      triggerPrompt(
        'Brand URL Change Warning',
        <div>
          <p className="mb-3">
            This creative is currently attached to other order lines that are
            already deployed.
          </p>
          <p className="mb-3">
            <strong>
              Changing the brand URL will affect ALL order lines using this
              creative as soon as they are redeployed.
            </strong>
          </p>
          <p>Are you sure you want to continue?</p>
        </div>,
        () => handleUpdateBrandURL(brandUrls, modifiedOL),
        { confirmBtnKind: 'danger' },
      )
    } else {
      handleUpdateBrandURL(brandUrls, modifiedOL)
    }
  }

  const handleRemoveBrandURL = (modifiedOL: ModifiedOrderLineType) => {
    handleAddChange({
      ...modifiedOL,
      brandUrls: undefined,
      brandUrlEdited: true,
    })
  }

  const hasUpdates = modifiedOrderLines.some((modOL) => {
    if (modOL.tempId || modOL.brandUrlEdited) return true
    const matching = selectedOls.find(
      (selectedOl) => selectedOl.id === modOL.id,
    )
    if (!matching) return true
    // if any modOL has a different click thru than the original
    if (matching.clickThroughUrl !== modOL.clickThroughUrl) {
      return true
    }
    const originalCreatives = matching.creatives
    const modCreatives = modOL.creatives
    if (
      originalCreatives &&
      modCreatives &&
      originalCreatives.length !== modCreatives.length
    )
      return true

    return !originalCreatives?.every((originalCreative) => {
      return modCreatives?.find((modAud) => modAud.id === originalCreative.id)
    })
  })

  if (view === 'create') {
    const selectedTitle = selectedCreativeType?.label
    const selected = selectedCreativeType?.value
    return (
      <div
        className={`CreativeEdit__create flex flex-col CreativeEdit__create-${selected}`}
      >
        <div className="CreativeEdit__create-header relative flex justify-between gap-2">
          <TextHeader className="flex items-center gap-2 pt-2" type={5}>
            <span>Create</span>
            <span className="text-tint-gray-500 scale-y-150">&#62;</span>
            {selectedTitle}
          </TextHeader>
        </div>
        {selected && ['native', 'vast', 'adTag'].includes(selected) && (
          <GoBackButton
            onClick={() => setView(undefined)}
            position="top-right"
            classWrap={`CreativeEdit__create--goback ${selected}`}
            className={hasUpdates ? 'ripples' : ''}
          />
        )}
        <div className="CreativeEdit__create-wrapper">
          {(() => {
            if (
              (selected === 'video' ||
                selected === 'banner' ||
                selected === 'ott' ||
                selected === 'html5') &&
              currentOrg?.id
            )
              return (
                <CreativeUpload
                  currentOrg={currentOrg?.id}
                  onUpload={handleAddCreative}
                  onClose={() => setView(undefined)}
                  uploadType={typeGetter(selected)}
                  isPoliticalInCampaign={isPolitical && isInCampaignCreation}
                />
              )
            if (selected === 'native')
              return (
                <NativeBannerCreate handleAddCreative={handleAddCreative} />
              )
            if (selected === 'vast')
              return (
                <AdTagCreate
                  type="video"
                  handleSelect={handleAddCreative}
                  isPoliticalInCampaign={isPolitical && isInCampaignCreation}
                />
              )
            if (selected === 'adTag')
              return (
                <AdTagCreate
                  type="banner"
                  handleSelect={handleAddCreative}
                  isPoliticalInCampaign={isPolitical && isInCampaignCreation}
                />
              )
          })()}
        </div>
      </div>
    )
  }
  if (view === 'library') {
    const attachedCreatives = modifiedOrderLines
      .map((ol) => ol.creatives)
      .flat()
      ?.reduce((acc: Campaignservicev1Creative[], currentCreative) => {
        if (
          !currentCreative ||
          !!acc.find((creative) => creative.id === currentCreative.id)
        )
          return acc
        return [...acc, currentCreative]
      }, [])
    const filterMaker = () => {
      if (!filterRules) {
        return undefined
      }
      if (filterRules === 'CREATIVE_ADTYPE_BANNER') {
        return [
          'CREATIVE_TYPE_BANNER',
          'CREATIVE_TYPE_HTML5',
          'CREATIVE_TYPE_AD_TAG',
          'CREATIVE_TYPE_NATIVE_BANNER',
        ] as Creativeservicev1Type[]
      }
      if (filterRules === 'CREATIVE_ADTYPE_VIDEO') {
        return [
          'CREATIVE_TYPE_OTT',
          'CREATIVE_TYPE_VIDEO',
          'CREATIVE_TYPE_VAST_TAG',
        ] as Creativeservicev1Type[]
      }
      if (filterRules === 'CREATIVE_ADTYPE_OTT') {
        return ['CREATIVE_TYPE_OTT'] as Creativeservicev1Type[]
      }
      if (filterRules) return undefined
    }
    return (
      <>
        <div className="CreativeEdit__wrap relative flex flex-col items-center justify-end gap-4 pb-2">
          <GoBackButton
            onClick={() => setView(undefined)}
            position="top-right"
            classWrap="Library--goback"
            className={hasUpdates ? 'ripples' : ''}
          />

          <CreativesLibraryPage
            className="CreativeEdit p-0"
            selectedCreatives={attachedCreatives}
            addToCreativeSet={handleAddCreative}
            removeFromCreativeSet={(creativeId) => {
              // for each order line, remove this creative that matches the creativeId
              const fullCreative = attachedCreatives.find(
                (c) => c.id === creativeId,
              )

              if (fullCreative) {
                modifiedOrderLines.forEach((modOL) => {
                  handleRemoveCreative(modOL, fullCreative)
                })
              }
            }}
            filterByType={filterMaker()}
            modalPreview
          />
        </div>
      </>
    )
  }

  return (
    <div className={`CreativeEdit ${className}`}>
      <AssetEditHeader
        selectedType={selectedCreativeType}
        setSelectedType={setSelectedCreativeType}
        handleAddCreative={handleAddCreative}
        setView={setView}
        modifiedOrderLines={modifiedOrderLines}
      />
      <CreativeEditOLTable
        modifiedOrderLines={modifiedOrderLines}
        handleRemoveCreative={handleRemoveCreative}
        handleUpdateClickThru={handleUpdateClickThru}
        handleUpdateBrandURL={handleUpdateBrandURLWithConfirmation}
        handleRemoveBrandURL={handleRemoveBrandURL}
      />

      <EditingCancelUpdateButtons
        onCloseModal={onCloseModal}
        handleUpdate={handleUpdate}
        updateDisabled={!hasUpdates || disabled}
      />

      {/* Brand URL Selection Dialog */}
      {showBrandUrlDialog && (
        <Modal
          offClick={() => {
            setShowBrandUrlDialog(false)
            setDisabled(false)
          }}
          header="Choose Brand URLs for New Creatives"
        >
          <div className="max-w-md">
            <p className="mb-2">
              You've added new creatives to order lines that have existing brand
              URLs. Select which brand URL (if any) should be applied to each
              creative by checking the corresponding checkbox.
            </p>

            <div className="max-h-64 space-y-4 overflow-y-auto">
              {creativesNeedingBrandUrl.map((item, index) => (
                <div
                  key={`${item.creative.id}_${index}`}
                  className="border-primary bg-tint-khaki-50 rounded border p-4"
                >
                  <div className="mb-3">
                    <Text className="text-sm font-medium">
                      {item.creative.name || 'Unnamed Creative'}
                    </Text>
                    <br />
                    <Text className="mb-2 text-xs">
                      Order Lines: {item.orderLineNames.join(', ')}
                    </Text>
                  </div>

                  {/* Brand URL Selection with Checkboxes */}
                  <div className="space-y-2">
                    <Text className="text-tint-gray-600 text-xs font-medium">
                      Choose one brand URL (selecting one will uncheck others):
                    </Text>
                    {item.availableBrandUrls.map((brandUrlOption) => (
                      <div
                        key={brandUrlOption.url}
                        className="flex items-center gap-2"
                      >
                        <Checkbox
                          checked={item.selectedBrandUrls?.includes(
                            brandUrlOption.url,
                          )}
                          onChange={(checked) => {
                            setCreativesNeedingBrandUrl((prev) =>
                              prev.map((prevItem, prevIndex) => {
                                if (prevIndex !== index) return prevItem

                                if (checked) {
                                  // If checking this box, uncheck all others (only one allowed)
                                  return {
                                    ...prevItem,
                                    selectedBrandUrls: [brandUrlOption.url],
                                  }
                                } else {
                                  // If unchecking, remove from array
                                  return {
                                    ...prevItem,
                                    selectedBrandUrls: prevItem.selectedBrandUrls.filter(
                                      (url) => url !== brandUrlOption.url,
                                    ),
                                  }
                                }
                              }),
                            )
                          }}
                        />
                        <div className="flex-1">
                          <Text className="break-all text-xs">
                            {brandUrlOption.url}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button
                onClick={() => {
                  setShowBrandUrlDialog(false)
                  setDisabled(false)
                }}
              >
                Cancel
              </Button>
              <Button
                kind="primary"
                onClick={async () => {
                  setShowBrandUrlDialog(false)
                  await performUpdate()
                }}
              >
                Apply & Save
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
